import { useEffect } from "react";
import { ArrowLeft, ArrowRight, ArrowDown, ExternalLink } from "lucide-react";
import BrandMark from "./BrandMark";
import { StatusPill } from "./Reveal";
import { TECH_NAV } from "../data/detailPages";

const SITE_URL = process.env.REACT_APP_SITE_URL;
const OG_IMAGE = `${SITE_URL}/assets/founder/airobotcamstudio-logo.png`;

function DetailSection({ section, index }) {
  return (
    <section data-testid={`detail-section-${index}`} className="mt-12">
      {section.heading && (
        <h2 className="font-display text-xl font-medium uppercase tracking-[0.12em] text-white">
          {section.heading}
        </h2>
      )}
      {section.paragraphs && (
        <div className={`space-y-4 text-sm leading-relaxed text-[#C5CBD5] ${section.heading ? "mt-4" : ""}`}>
          {section.paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      )}
      {section.bullets && (
        <ul className={`grid gap-2.5 sm:grid-cols-2 ${section.heading ? "mt-5" : ""}`}>
          {section.bullets.map((b) => (
            <li key={b} className="flex items-start gap-3 text-sm leading-relaxed text-[#C5CBD5]">
              <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rotate-45 bg-cyan-300/70" />
              {b}
            </li>
          ))}
        </ul>
      )}
      {section.steps && (
        <ol className={`space-y-3 ${section.heading ? "mt-5" : ""}`}>
          {section.steps.map((s, i) => (
            <li key={s} className="flex items-start gap-4 rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3.5">
              <span className="font-display shrink-0 text-sm font-semibold text-cyan-300">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-sm leading-relaxed text-white/65">{s}</span>
            </li>
          ))}
        </ol>
      )}
      {section.flow && (
        <div className={section.heading ? "mt-5" : ""}>
          {section.flow.map((step, i) => (
            <div key={step}>
              <div className="rounded-lg border border-cyan-300/15 bg-cyan-300/[0.04] px-4 py-3 text-center font-display text-[12px] font-semibold uppercase tracking-[0.2em] text-cyan-100/90">
                {step}
              </div>
              {i < section.flow.length - 1 && (
                <div className="flex justify-center py-1.5">
                  <ArrowDown size={14} className="text-cyan-300/50" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {section.stack && (
        <div className={section.heading ? "mt-5" : ""}>
          {section.stack.map((item, i) => (
            <div key={item}>
              <div
                className={`rounded-lg border px-4 py-3 text-center font-display text-[12px] font-semibold uppercase tracking-[0.18em] ${
                  i === section.stack.length - 1
                    ? "border-cyan-300/50 bg-cyan-300/10 text-cyan-100"
                    : "border-white/10 bg-white/[0.02] text-white/65"
                }`}
              >
                {item}
              </div>
              {i < section.stack.length - 1 && (
                <div className="flex justify-center py-0.5 font-display text-sm font-light text-cyan-300/60">
                  {i === section.stack.length - 2 ? "↓" : "+"}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function DetailPage({ page }) {
  useEffect(() => {
    const path = window.location.pathname.replace(/\/+$/, "");
    const url = `${SITE_URL}${path}`;
    const prevTitle = document.title;
    const descEl = document.head.querySelector('meta[name="description"]');
    const prevDesc = descEl ? descEl.getAttribute("content") : null;

    document.title = page.metaTitle;
    if (descEl) descEl.setAttribute("content", page.metaDesc);

    const created = [];
    const modified = [];
    const upsertMeta = (attr, value, content) => {
      let el = document.head.querySelector(`meta[${attr}="${value}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, value);
        document.head.appendChild(el);
        created.push(el);
      } else {
        modified.push({ el, prev: el.getAttribute("content") });
      }
      el.setAttribute("content", content);
    };
    upsertMeta("property", "og:title", page.metaTitle);
    upsertMeta("property", "og:description", page.metaDesc);
    upsertMeta("property", "og:type", "article");
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", OG_IMAGE);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    const canonicalCreated = !canonical;
    const prevCanonical = canonicalCreated ? null : canonical.getAttribute("href");
    if (canonicalCreated) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      if (descEl && prevDesc) descEl.setAttribute("content", prevDesc);
      modified.forEach(({ el, prev }) => el.setAttribute("content", prev));
      created.forEach((el) => el.remove());
      if (canonicalCreated) canonical.remove();
      else if (prevCanonical) canonical.setAttribute("href", prevCanonical);
    };
  }, [page]);

  return (
    <div data-testid="detail-page" className="min-h-screen bg-[#050505] text-white">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-black/70 backdrop-blur-2xl">
        <div className="mx-auto flex h-[64px] max-w-4xl items-center justify-between px-6">
          <a
            href="/"
            data-testid="detail-logo"
            className="flex items-center gap-2.5 font-display text-sm font-semibold tracking-[0.25em] text-white"
          >
            <BrandMark className="h-7 w-7 rounded-md" testid="detail-logo-mark" />
            AIROBOTCAM<span className="text-cyan-300">STUDIO</span>
          </a>
          <a
            href={page.back.href}
            data-testid="detail-back"
            className="flex shrink-0 items-center gap-2 text-[13px] font-medium text-cyan-200/70 transition-colors duration-300 hover:text-cyan-100"
          >
            <ArrowLeft size={15} />
            <span className="sm:hidden">Back</span>
            <span className="hidden sm:inline">{page.back.label}</span>
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16 lg:py-24">
        <div className="flex items-center gap-4">
          <BrandMark className="h-12 w-12 rounded-xl border border-white/10" testid="detail-brand-logo" />
          <p data-testid="detail-tag" className="font-display text-[11px] font-medium uppercase tracking-[0.35em] text-cyan-300/90">
            {page.tag}
          </p>
        </div>

        <h1
          data-testid="detail-title"
          className="font-display mt-7 text-3xl font-light leading-tight tracking-tighter text-white sm:text-4xl lg:text-5xl"
        >
          {page.title}
        </h1>
        {page.subtitle && (
          <p data-testid="detail-subtitle" className="mt-4 text-base leading-relaxed text-[#C5CBD5]">
            {page.subtitle}
          </p>
        )}
        {page.status && (
          <div className="mt-6">
            <StatusPill data-testid="detail-status" tone="amber">
              {page.status}
            </StatusPill>
          </div>
        )}

        {page.image && (
          <figure className="mt-8">
            <div className="overflow-hidden rounded-2xl border border-white/10">
              <img
                src={page.image.src}
                alt={page.image.alt}
                loading="lazy"
                data-testid="detail-image"
                className="aspect-video w-full object-cover"
              />
            </div>
            <figcaption className="mt-2.5 text-[11px] uppercase tracking-[0.2em] text-white/50">
              {page.image.caption}
            </figcaption>
          </figure>
        )}

        {page.sections.map((s, i) => (
          <DetailSection key={i} section={s} index={i} />
        ))}

        {page.note && (
          <p data-testid="detail-note" className="mt-12 border-l-2 border-cyan-300/40 pl-5 text-xs leading-relaxed text-white/70">
            {page.note}
          </p>
        )}

        {page.externalLinks && (
          <div className="mt-12 space-y-3">
            {page.externalLinks.map((l, i) => (
              <a
                key={l.href}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`detail-external-${i}`}
                className="group flex w-fit items-center gap-2 text-sm font-semibold text-cyan-300/90 underline-offset-4 transition-colors duration-300 hover:text-cyan-100 hover:underline hover:decoration-cyan-300/40"
              >
                {l.label}
                <ExternalLink size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                <span className="text-[10px] font-medium uppercase tracking-[0.15em] text-white/55">(external)</span>
              </a>
            ))}
          </div>
        )}

        <div className="mt-14 flex flex-col gap-3 sm:flex-row">
          <a
            href={page.cta.href}
            data-testid="detail-cta"
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-cyan-300 px-7 py-3.5 text-sm font-bold text-black transition-transform duration-300 hover:-translate-y-0.5 hover:bg-cyan-200 active:scale-[0.98]"
          >
            {page.cta.label}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href={page.back.href}
            data-testid="detail-back-bottom"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-cyan-300/30 px-7 py-3.5 text-sm font-semibold text-cyan-200/90 transition-all duration-300 hover:-translate-y-0.5 hover:border-cyan-300/60 hover:text-cyan-100 active:scale-[0.98]"
          >
            <ArrowLeft size={15} /> {page.back.label}
          </a>
        </div>

        {(page.nav || page.navLabel) && (
          <nav data-testid="detail-nav" className="mt-14 border-t border-white/10 pt-8">
            {page.navLabel && (
              <p className="font-display mb-4 text-[11px] font-semibold uppercase tracking-[0.3em] text-white/70">
                {page.navLabel}
              </p>
            )}
            <div className="flex flex-wrap gap-2.5">
              {(page.nav || TECH_NAV).map(([label, href]) => {
                const current = href === window.location.pathname.replace(/\/+$/, "");
                return (
                  <a
                    key={href}
                    href={href}
                    data-testid={`detail-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    aria-current={current ? "page" : undefined}
                    className={`rounded-full border px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.15em] transition-colors duration-300 ${
                      current
                        ? "border-cyan-300/60 bg-cyan-300/10 text-cyan-200"
                        : "border-cyan-300/20 text-cyan-200/60 hover:border-cyan-300/50 hover:text-cyan-100"
                    }`}
                  >
                    {label}
                  </a>
                );
              })}
            </div>
          </nav>
        )}
      </main>

      <footer className="border-t border-white/10">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-4 px-6 py-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-white/55">
            AIROBOTCAMSTUDIO LTD · UK Company No. 17410621
          </p>
          <a href="/" className="text-xs text-cyan-200/60 transition-colors hover:text-cyan-100">
            Back to Home
          </a>
        </div>
      </footer>
    </div>
  );
}
